// Modal simple para crear nueva dieta clinica (Sprint 8 mejora UX).
// Consume POST /nutricionista/dietas nuevo (endpoint S8).
// USO RESTRINGIDO: solo cuando contrato Avante incorpora variante clinica.
import { useState } from 'react'
import type { DietaCreateBody } from '../../services/nutricionista'
import { ModalShell } from '../ui/ModalShell'

interface Props {
  onClose: () => void
  onSubmit: (body: DietaCreateBody) => Promise<void>
}

export default function NuevaDietaModal({ onClose, onSubmit }: Props) {
  const [codigo, setCodigo] = useState('')
  const [nombre, setNombre] = useState('')
  const [calorias, setCalorias] = useState<string>('')
  const [chopct, setChopct] = useState<string>('')
  const [protpct, setProtpct] = useState<string>('')
  const [grasapct, setGrasapct] = useState<string>('')
  const [observaciones, setObservaciones] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      const body: DietaCreateBody = {
        codigo: codigo.trim().toUpperCase(),
        nombre: nombre.trim(),
        calorias: calorias ? parseInt(calorias) : null,
        cho_pct: chopct ? parseFloat(chopct) : null,
        prot_pct: protpct ? parseFloat(protpct) : null,
        grasa_pct: grasapct ? parseFloat(grasapct) : null,
        menu_referencia: null,  // S8: campo eliminado del form (legacy Sprint 5 no usado en operacion)
        observaciones: observaciones.trim() || null,
        activo: true,
      }
      await onSubmit(body)
      onClose()
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Error al crear dieta')
    } finally {
      setLoading(false)
    }
  }

  return (
    <ModalShell
      title="Nueva dieta clínica"
      subtitle="Uso restringido. Solo agregar nuevas dietas cuando el contrato hospital lo incorpore. Las 15 dietas estándar ya existen."
      onClose={onClose}
      closeDisabled={loading}
      maxWidth={560}
    >
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-sm font-medium mb-1">Código *</label>
              <input
                type="text"
                value={codigo}
                onChange={(e) => setCodigo(e.target.value)}
                required
                maxLength={20}
                placeholder="POSTQUIRURGICA"
                className="w-full p-2 border rounded uppercase focus:outline-none focus:ring-2 focus:ring-avante-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Nombre *</label>
              <input
                type="text"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                required
                maxLength={100}
                placeholder="POST-QUIRURGICA"
                className="w-full p-2 border rounded uppercase focus:outline-none focus:ring-2 focus:ring-avante-primary"
                style={{ textTransform: 'uppercase' }}
              />
            </div>
          </div>
          <div className="grid grid-cols-4 gap-2">
            <div>
              <label className="block text-sm font-medium mb-1">Kcal</label>
              <input
                type="number"
                value={calorias}
                onChange={(e) => setCalorias(e.target.value)}
                min={0}
                max={5000}
                placeholder="1800"
                className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-avante-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">CHO%</label>
              <input
                type="number"
                value={chopct}
                onChange={(e) => setChopct(e.target.value)}
                min={0}
                max={100}
                step={0.1}
                placeholder="55"
                className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-avante-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Prot%</label>
              <input
                type="number"
                value={protpct}
                onChange={(e) => setProtpct(e.target.value)}
                min={0}
                max={100}
                step={0.1}
                placeholder="20"
                className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-avante-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Grasa%</label>
              <input
                type="number"
                value={grasapct}
                onChange={(e) => setGrasapct(e.target.value)}
                min={0}
                max={100}
                step={0.1}
                placeholder="25"
                className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-avante-primary"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Observaciones</label>
            <textarea
              value={observaciones}
              onChange={(e) => setObservaciones(e.target.value)}
              rows={3}
              placeholder="Indicaciones clínicas específicas..."
              className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-avante-primary"
            />
          </div>
          {error && <p className="text-avante-danger text-sm">{error}</p>}
          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="flex-1 px-4 py-2 border rounded font-medium hover:bg-gray-50 disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-avante-primary text-white px-4 py-2 rounded font-semibold hover:bg-avante-accent disabled:opacity-50"
            >
              {loading ? 'Creando...' : 'Crear dieta'}
            </button>
          </div>
        </form>
    </ModalShell>
  )
}
